import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const DEFAULT_FAQS = [
  { q: "What areas of London do you cover?", a: "We're based in Wembley Park HA9 and regularly work across Ealing (W5), Richmond (TW9/TW10), Chiswick (W4), Wimbledon (SW19), Hampstead (NW3) and Croydon (CR0/CR2). Get in touch if you're elsewhere in Greater London — we travel for the right project." },
  { q: "How much does a renovation, extension or loft conversion cost in London?", a: "It depends on size, finish level and structural complexity. Indicative 2026 London ranges: house extensions £2,800–£5,200/m², loft conversions £55k–£145k, full house renovations £180k–£800k+. Try our AI Cost Estimator for an instant ballpark, or book a free consultation for a tailored quote." },
  { q: "Do you handle planning permission and party-wall agreements?", a: "Yes — every contract includes full management of planning applications, party-wall surveyor instructions and Building Control submissions. You don't deal with any of it." },
  { q: "How long will my project take?", a: "Single bathroom: 3–5 weeks. Loft conversion: 10–14 weeks. Single-storey extension: 12–16 weeks. Whole-home renovation: 16–32 weeks. We give you a firm programme before contract signing — and weekly reports during the build." },
  { q: "What guarantees do you offer?", a: "Every project carries a 10-year structural guarantee and a 2-year finishes warranty. We're a registered UK limited company with full Public Liability Insurance." },
  { q: "Do you offer flexible payment plans?", a: "Yes — we stage payments to match build milestones (typically 6–8 stages) rather than asking for large upfront sums. For larger projects we can also discuss extended payment terms — just ask." },
];

/**
 * Builds a Schema.org FAQPage JSON-LD object — exported so any page that
 * renders this component can also include the JSON-LD in <head> via Seo.
 */
export function faqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export default function FaqSection({ faqs = DEFAULT_FAQS, title = "Answers, before you ask.", overline = "Frequently asked questions", background = "white", testId = "home-faq" }) {
  const bg = background === "slate" ? "bg-slate-50 border-y" : "bg-white border-t";
  return (
    <section className={`${bg} border-slate-200`} data-testid={testId}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="overline mb-4">{overline}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
          {title}
        </h2>
        <div className="mt-12 divide-y divide-slate-200">
          {faqs.map((f, i) => <Item key={i} q={f.q} a={f.a} idx={i} testId={`${testId}-${i}`} />)}
        </div>
      </div>
    </section>
  );
}

function Item({ q, a, idx, testId }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="py-5" data-testid={testId} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex justify-between items-start text-left gap-6" aria-expanded={open}>
        <span className="font-display text-lg md:text-xl font-semibold text-slate-900" itemProp="name">{q}</span>
        <ChevronDown size={20} className={`flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""} text-slate-500`} />
      </button>
      {open && (
        <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
          <p className="mt-4 text-slate-600 leading-relaxed" itemProp="text">{a}</p>
        </div>
      )}
    </div>
  );
}

export const HOME_FAQS = DEFAULT_FAQS;
